import { useEffect, useId } from 'react';
import type { ReactNode } from 'react';

type ModalProps = {
  title: string;
  onClose: () => void;
  /** When true, Esc and backdrop-click no-op (e.g. while saving). */
  disabled?: boolean;
  /** Tailwind max-width class; defaults to a 460px card. Pass a wider one
   *  (e.g. 'max-w-[560px]') for content-heavy dialogs. */
  maxWidthClass?: string;
  children: ReactNode;
};

/**
 * Shared admin dialog shell: backdrop + role="dialog" + Esc to close +
 * backdrop-click to close + the dark card chrome. Each caller owns its own
 * body content and focus management (the consumer's first input/button refs
 * itself on mount).
 */
export function Modal({ title, onClose, disabled = false, maxWidthClass = 'max-w-[460px]', children }: ModalProps) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disabled) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, onClose]);

  return (
    // Backdrop click is a pointer-only supplement to the Escape handler
    // above — keyboard users already have an equivalent close path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !disabled) onClose();
      }}
    >
      <div
        className={`max-h-[85vh] w-full ${maxWidthClass} overflow-y-auto rounded-lg border border-white/15 bg-[#2a2237] p-6 text-white shadow-[0_20px_60px_rgba(0,0,0,0.6)]`}
      >
        <h3 id={titleId} className="font-['Orbitron',sans-serif] text-[18px] uppercase tracking-[0.04em]">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
